import { useRemote } from '../../../Utils/Utils';
import { useEffect, useContext, useMemo, useState } from 'react';
import { displayValueFunction, analyticsExportFilename } from '../AnalyticsUtils';
import { BannerContext } from '../../../InternalApiApp';
import AnalyticsBarChart from '../Charts/AnalyticsBarChart';
import LoadingIcon from '../../../components/LoadingIcon';
import AnalyticsHeader from './AnalyticsHeader';
import moment from 'moment';


const BarChartWrapper = ({ label, endpoint, guidelines, barKey1, barKey2, /*textAnchor, angle, textHeight, dx,*/ dy, direction, tail, removeDatum, passedTimeFrame, mb, axisLabelClass, customeDate }) => {
    const remote = useRemote();
    const { bannerAdditionalInfo } = useContext(BannerContext)
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState([]);
    const [timeFrame, setTimeFrame] = useState(passedTimeFrame);
    const [rawData, setRowData] = useState(null)

    useEffect(() => {
        if (bannerAdditionalInfo && customeDate && customeDate.from && customeDate.to) {
            setLoading(true);
            const end = moment(customeDate.to).format('YYYY-MM-DD');
            const start = moment(customeDate.from).format('YYYY-MM-DD');
            const products = bannerAdditionalInfo?.platforms.map(platform =>
                platform.products.map(p => p.selected ? `product=${p.id}` : null)
                    .filter(e => e !== null)
                    .join("&")
            )
            const path = endpoint.replace("${end}", end).replace("${date}", start).replace("${products}", products ? "&" + products : "")

            remote.get(path)
                .then((uniquePortfoliosResponse) => {
                    const isArrayResponse = Array.isArray(uniquePortfoliosResponse);

                    const list = [];
                    if (isArrayResponse) {
                        for (var i = 0; i < uniquePortfoliosResponse.length; i++) {
                            const newObj = {}
                            guidelines.forEach(g => {
                                const obj = displayValueFunction(uniquePortfoliosResponse[i], g);
                                if (!newObj[g.keyName]) {
                                    newObj[g.keyName] = obj[g.keyName];
                                }
                            });
                            list.push(newObj);
                        }
                    } else if (typeof uniquePortfoliosResponse === 'object' && uniquePortfoliosResponse !== null) {
                        const arr = Object.entries(uniquePortfoliosResponse);
                        arr.forEach(([key, value]) => {
                            const newObj = {};
                            guidelines.forEach((g, index) => {
                                if (index === 0) {
                                    const name = `${key} ${g.keyName}`
                                    newObj.name = name;
                                } else {
                                    newObj[g.keyName] = value;
                                }
                            });
                            list.push(newObj);
                        });
                    }

                    let datums = list

                    if (removeDatum) {
                        datums = list.filter(item => {
                            return removeDatum.label !== item.name
                        });
                    }

                    if (tail && datums.length > tail.index) {
                        const trimmedList = datums.slice(0, tail.index);
                        const datumsToBeAdded = datums.slice(tail.index)

                        const lastDatum = barKey2
                            ? datumsToBeAdded.reduce((a, c) => ({ ...a, name: tail.label, [barKey1]: a[barKey1] ? a[barKey1] + c[barKey1] : c[barKey1], [barKey2]: a[barKey2] ? a[barKey2] + c[barKey2] : c[barKey2] }), {})
                            : datumsToBeAdded.reduce((a, c) => ({ ...a, name: tail.label, [barKey1]: a[barKey1] ? a[barKey1] + c[barKey1] : c[barKey1] }), {})

                        datums = [...trimmedList, lastDatum];

                        if (direction && direction === 'vertical') {
                            datums.reverse();
                        }
                        setData(datums);

                    } else {
                        const sortedList = direction && direction === 'vertical' ? datums.reverse() : datums;
                        setData(sortedList);
                    }

                    setRowData(uniquePortfoliosResponse)
                    setLoading(false);

                }).catch((e) => {
                    console.error("Error fetching unique portfolios per session data:", e);
                    setLoading(false);
                });
        }
    }, [bannerAdditionalInfo, timeFrame, customeDate, passedTimeFrame, remote])

    // function tickFormatter(value) {}

    function renderTooltip(payload) {
        if (!payload || !payload.active || !payload.payload || payload.payload.length === 0) return null;
        let value = payload.payload[0].payload[barKey1];
        let value2 = barKey2 ? payload.payload[0].payload[barKey2] : null;
        let name = payload.payload[0].payload.name;

        return (
            <div style={{ padding: 10, borderRadius: '0.25rem', boxShadow: '0 1px 5px rgba(0, 0, 0, 0.15)', display: 'flex', gap: '0.5rem', flexDirection: 'column', backgroundColor: '#fff' }}>
                <div type="body" style={{ display: "flex", alignItems: "center", gap: 8, color: "#0E8EFD", fontWeight: 500, fontSize: '1.25rem' }}>
                    {/* <div style={{ backgroundColor: "#0f8efd", borderRadius: "50%", width: 12, height: 12 }}></div> */}
                    {value + " " + barKey1}
                </div>
                {value2 &&
                    <div type="body" style={{ display: "flex", alignItems: "center", gap: 8, color: "#62d9b7", fontWeight: 500, fontSize: '1.25rem' }}>
                        {/* <div style={{ backgroundColor: "#0f8efd", borderRadius: "50%", width: 12, height: 12 }}></div> */}
                        {value2 + " " + barKey2}
                    </div>
                }
                <div type="body" style={{ fontSize: '1rem', color: '#333B5A' }}>{"viewed " + name}</div>
            </div>
        );
    }


    const exportHeaders = useMemo(() => {
        if (!Array.isArray(guidelines) || guidelines.length === 0) return [];
        return guidelines.map((g, index) => ({
            label: typeof g.keyName === 'string' ? g.keyName.charAt(0).toUpperCase() + g.keyName.slice(1) : g.keyName,
            key: index === 0 ? 'name' : g.keyName,
        }));
    }, [guidelines]);

    const exportFilename = useMemo(
        () => analyticsExportFilename('unique-portfolios-per-session', customeDate),
        [customeDate]
    );

    return (
        <>
            <AnalyticsHeader
                timeFrame={customeDate ? passedTimeFrame : timeFrame}
                setTimeFrame={setTimeFrame}
                mb={mb}
                exportHeaders={exportHeaders}
                exportData={data}
                exportFilename={exportFilename}
                exportLoading={loading}
            >{label ? label : "Search Terms"}</AnalyticsHeader>
            {loading ?
                <div style={{ height: 300, display: 'flex' }}>
                    <LoadingIcon centered />
                </div>
                : rawData?.length === 0
                    ? <div>
                        <p className='colour-text-neutral-dark-50'>No data to display</p>
                        <div style={{ height: 170 }}></div>
                    </div>
                    : <AnalyticsBarChart
                        data={data}
                        barKey1={barKey1}
                        barKey2={barKey2}
                        renderTooltip={renderTooltip}
                        dy={dy}
                        direction={direction}
                        axisLabelClass={axisLabelClass}
                    // tickFormatter={tickFormatter}
                    // textAnchor={textAnchor}
                    // angle={angle}
                    // textHeight={textHeight}
                    // dx={dx}
                    />
            }
        </>
    );
}

export default BarChartWrapper;